import { ColumnMapping, ExcelProcessorConfig, HeaderConfig, ParsedExcelData, ValidationError } from '@/lib/excel';

export interface ImportBody {
  ma_phi: string;
  ten_phi: string;
  ten_khac: string | null;
  nhom_phi_1: string | null;
  nhom_phi_2: string | null;
  nhom_phi_3: string | null;
  bo_phan: string | null;
  trang_thai: number;
}

const HEADERS = {
  ma_phi: 'Mã phí*',
  ten_phi: 'Tên phí*',
  ten_khac: 'Tên khác',
  nh_phi1: 'Nhóm phí 1',
  nh_phi2: 'Nhóm phí 2',
  nh_phi3: 'Nhóm phí 3',
  bo_phan: 'Bộ phận',
  trang_thai: 'Trạng thái'
};

const HEADER_CONFIG: HeaderConfig[] = [
  { header: HEADERS.ma_phi, key: 'ma_phi', type: 'main' },
  { header: HEADERS.ten_phi, key: 'ten_phi', type: 'main' },
  { header: HEADERS.ten_khac, key: 'ten_khac', type: 'main' },
  { header: HEADERS.nh_phi1, key: 'nh_phi1', type: 'main' },
  { header: HEADERS.nh_phi2, key: 'nh_phi2', type: 'main' },
  { header: HEADERS.nh_phi3, key: 'nh_phi3', type: 'main' },
  { header: HEADERS.bo_phan, key: 'bo_phan', type: 'main' },
  { header: HEADERS.trang_thai, key: 'trang_thai', type: 'main' }
];

const UUID_FIELDS = {
  main: ['nh_phi1', 'nh_phi2', 'nh_phi3', 'bo_phan']
};

const getFieldName = (key: string) => {
  const config = HEADER_CONFIG.find(c => c.key === key);
  return config?.header?.replace('*', '').trim() || key;
};

type ExcelSection = Record<string, unknown[]>;
type UUIDLookupResponse = Partial<Record<string, ExcelSection>>;

const getVal = (
  uuid: UUIDLookupResponse | null | undefined,
  parsed: ExcelSection,
  section: string,
  field: string,
  i: number
) => (uuid?.[section]?.[field]?.[i] ?? parsed[field]?.[i] ?? undefined) as string | null | undefined;

const UUIDPayload = (data: ParsedExcelData) => {
  const build = (section: ExcelSection, fields: string[]) =>
    Object.fromEntries(
      fields.map(f => [f, (section[f] || []).map(val => (val === null || val === undefined ? '' : val))])
    );

  return {
    main: build(data.main || {}, UUID_FIELDS.main)
  };
};

const ValidateUUID = (
  response: UUIDLookupResponse | null | undefined,
  columnMapping: Record<string, ColumnMapping>
): ValidationError[] => {
  if (!response) return [];
  const errors: ValidationError[] = [];

  const data = response.main;
  if (!data) return errors;

  Object.entries(data).forEach(([field, values]) => {
    if (!Array.isArray(values)) return;
    const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === 'main');

    values.forEach((val, i) => {
      if (val === null) {
        errors.push({
          row: i + 2,
          field,
          message: `${getFieldName(field)} không hợp lệ`,
          section: 'main',
          columnIndex: mapping?.index
        });
      }
    });
  });

  return errors;
};

const TransformToBodies = (
  dataRows: unknown[][],
  parsedData: ParsedExcelData,
  uuidResponse: UUIDLookupResponse | null | undefined,
  context: unknown
): ImportBody[] => {
  console.log('TransformToBodies parsedData:', parsedData);
  if (!parsedData) {
    console.error('parsedData is undefined');
    return [];
  }
  const { main = {} } = parsedData;
  const bodies: ImportBody[] = [];

  for (let i = 0; i < dataRows.length; i++) {
    const ma_phi = main.ma_phi?.[i]?.toString() || '';
    const ten_phi = main.ten_phi?.[i]?.toString() || '';

    // Skip if required fields are missing
    if (!ma_phi || !ten_phi) continue;

    bodies.push({
      ma_phi,
      ten_phi,
      ten_khac: main.ten_khac?.[i]?.toString() || '',
      nhom_phi_1: getVal(uuidResponse, main, 'main', 'nh_phi1', i) || '',
      nhom_phi_2: getVal(uuidResponse, main, 'main', 'nh_phi2', i) || '',
      nhom_phi_3: getVal(uuidResponse, main, 'main', 'nh_phi3', i) || '',
      bo_phan: getVal(uuidResponse, main, 'main', 'bo_phan', i) || '',
      trang_thai: parseInt(main.trang_thai?.[i] || '1', 10)
    });
  }

  return bodies;
};

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  RequiredFields: [
    { key: 'ma_phi', type: 'main', label: 'Mã phí' },
    { key: 'ten_phi', type: 'main', label: 'Tên phí' }
  ],
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};
